import {
  createFileRoute,
  Outlet,
  redirect,
  useChildMatches,
  useNavigate,
} from '@tanstack/react-router'
import { ImportStatementDialog } from '@/features/accounting-bank/import-wizard'
import {
  STATEMENT_STATUSES,
  type StatementFilters,
  type StatementStatusValue,
} from '@/features/accounting-reconciliation/api'
import { StatementList } from '@/features/accounting-reconciliation/statement-list'
import { hasGrant } from '@/lib/me'

function isStatementStatus(value: unknown): value is StatementStatusValue {
  return typeof value === 'string' && (STATEMENT_STATUSES as readonly string[]).includes(value)
}

/**
 * Route shell for the bank reconciliation list.
 *
 * The list's search and status filter live in the URL search params so a
 * filtered view is linkable and survives a reload; `StatementList` matches
 * them client-side, because the endpoint takes no query parameters.
 *
 * `reconciliation.$statementId.tsx` is a CHILD of this route in flat file
 * routing, so without an Outlet it never mounts and its URL silently renders
 * this list instead (the same shape as `bills.tsx`).
 */
export const Route = createFileRoute('/_authenticated/accounting/reconciliation')({
  validateSearch: (search: Record<string, unknown>): StatementFilters => ({
    q: typeof search.q === 'string' && search.q ? search.q : undefined,
    status: isStatementStatus(search.status) ? search.status : undefined,
  }),
  beforeLoad: ({ context, location }) => {
    if (!hasGrant(context.queryClient, 'accounting_reconciliation.view')) {
      throw redirect({ to: '/accounting' })
    }
    if (
      location.pathname === '/accounting/reconciliation' ||
      location.pathname === '/accounting/reconciliation/'
    ) {
      throw redirect({ to: '/accounting/bank-accounts' })
    }
  },
  component: AccountingReconciliationRoute,
})

function AccountingReconciliationRoute() {
  const navigate = useNavigate()
  const children = useChildMatches()
  const params = Route.useSearch()

  if (children.length > 0) {
    return <Outlet />
  }

  return (
    <StatementList
      params={params}
      onParamsChange={(next) => void navigate({ to: '/accounting/reconciliation', search: next })}
      renderImportAction={({ companyId, onImported }) => (
        <ImportStatementDialog companyId={companyId} onImported={() => onImported()} />
      )}
    />
  )
}
